import React from "react";

import { AdtActionCard } from "@/components/adtActionCard/adtActionCard.component";
import { AdtAwards } from "@/components/adtAwards/adtAwards.component";
import { AdtCustomerReviewsSection } from "@/components/adtCustomerReviewsSection/adtCustomerReviewsSection.component";
import { AdtImageHero } from "@/components/adtImageHero/adtImageHero.component";
import { AdtMonitoringBanner } from "@/components/adtMonitoringBanner/adtMonitoringBanner.component";
import { AdtProfessionalMonitoring } from "@/components/adtProfessionalMonitoring/adtProfessionalMonitoring.component";
import { AdtQuizPromo } from "@/components/adtQuizPromo/adtQuizPromo.component";
import { AdtSocialProof } from "@/components/adtSocialProof/adtSocialProof.component";
import { AdtTwoWays } from "@/components/adtTwoWays/adtTwoWays.component";
import { AdtVideoHero } from "@/components/adtVideoHero/adtVideoHero.component";
import { AppInfo } from "@/components/appInfo/appInfo.component";
import { AppRatingsComparisonTable } from "@/components/appRatingsComparisonTable/appRatingsComparisonTable.component";
import { ArticleGroup } from "@/components/articleGroup/articleGroup.component";
import { BuildProtection } from "@/components/buildProtection/buildProtection.component";
import { ButtonBuilder } from "@/components/buttonBuilder/buttonBuilder.component";
import { CompareCompetitorTableSection } from "@/components/compareCompetitorTableSection/compareCompetitorTableSection.component";
import { ComparePlanTableSection } from "@/components/comparePlanTableSection/comparePlanTableSection.component";
import { CompareTableSection } from "@/components/compareTableSection/compareTableSection.component";
import { ContainerBuilder } from "@/components/containerBuilder/containerBuilder.component";
import { CustomerReviewsSection } from "@/components/customerReviewsSection/customerReviewsSection.component";
import { FaqSection } from "@/components/faqSection/faqSection.component";
import { FeaturedKits } from "@/components/featuredKits/featuredKits.component";
import { HeroSection } from "@/components/heroSection/heroSection.component";
import { ImageContentCard } from "@/components/imageContentCard/imageContentCard.component";
import { MultiColumnContent } from "@/components/multiColumnContent/multiColumnContent.component";
import { OfferCard } from "@/components/offerCard/offerCard.component";
import { OfferRedemption } from "@/components/offerRedemption/offerRedemption.component";
import { OptionCardGrid } from "@/components/optionCardGrid/optionCardGrid.component";
import { ProductCardListing } from "@/components/productCardListing/productCardListing.component";
import { QuizSection } from "@/components/quizSection/quizSection.component";
import { ReasonsToBuySection } from "@/components/reasonsToBuySection/reasonsToBuySection.component";
import { ReasonsToBuySection4Column } from "@/components/reasonsToBuySection/reasonsToBuySection4Column.component";
import { RecommendedProducts } from "@/components/recommendedProducts/recommendedProducts.component";
import { SeoParagraph } from "@/components/seoParagraph/seoParagraph.component";
import { SpotlightHero } from "@/components/spotlightHero/spotlightHero.component";
import { SuggestedForYouBlock } from "@/components/suggestedForYou/suggestedForYouBlock.component";
import { Testimonials } from "@/components/testimonials/testimonials.component";
import { TextBuilder } from "@/components/textBuilder/textBuilder.component";
import { TwoColumnAccordionMedia } from "@/components/twoColumnAccordionMedia/twoColumnAccordionMedia.component";
import { TwoColumnImageBenefits } from "@/components/twoColumnImageBenefits/twoColumnImageBenefits.component";
import { TwoColumnPromoSection } from "@/components/twoColumnPromoSection/twoColumnPromoSection.component";
import { VideoSection } from "@/components/videoSection/videoSection.component";
import { CarouselSection } from "@/components/carousel/carouselSection.component";

import { AppDownloadCallout } from "@/app/blu/adtplus-app/components/appDownloadCallout/appDownloadCallout.component";
import { FlexibleMonitoringAppSection } from "@/app/blu/adtplus-app/components/flexibleMonitoringAppSection/flexibleMonitoringAppSection.component";
import { TwoColumnHighestRatedApp } from "@/app/blu/adtplus-app/components/twoColumnHighestRatedApp/twoColumnHighestRatedApp.component";
import { ShopPromoBanner } from "@/app/blu/shop/components/shopPromoBanner/shopPromoBanner.component";
import { FreeQuoteInline } from "@/app/resources/components/freeQuoteInline/freeQuoteInline.component";
import { MedicalAlertSystemSection } from "@/components/medicalAlertSystemSection/medicalAlertSystemSection.component";
import { dataAttr } from "@/sanity/utils";

/**
 * Used by the <PageBuilder>, this component renders a the component that matches the block type.
 */
export default function BlockRenderer({
  block,
  index,
  pageId,
  pageType,
  pageName,
}: BlockProps) {
  // Block does exist
  if (typeof Blocks[block._type] !== "undefined") {
    const blockPath = `pageBuilder[_key=="${block._key}"]`;

    return (
      <div
        key={block._key}
        data-sanity={dataAttr({
          id: pageId,
          type: pageType,
          path: blockPath,
        }).toString()}>
        {React.createElement(Blocks[block._type], {
          key: block._key,
          block: block,
          index: index,
          pageId,
          pageType,
          pageName,
          path: blockPath,
        })}
      </div>
    );
  }
  // Block doesn't exist yet
  return React.createElement(
    () => (
      <div className="w-full rounded bg-gray-100 p-20 text-center text-gray-500">
        A &ldquo;{block._type}&rdquo; block hasn&apos;t been created
      </div>
    ),
    { key: block._key },
  );
}

const Blocks: BlocksType = {
  heroSection: HeroSection,
  offerRedemption: OfferRedemption,
  reasonsToBuySection: ReasonsToBuySection,
  reasonsToBuySection4Column: ReasonsToBuySection4Column,
  faqSection: FaqSection,
  recommendedProducts: RecommendedProducts,
  featuredKits: FeaturedKits,
  shopPromoBanner: ShopPromoBanner,
  imageContentCard: ImageContentCard,
  adtActionCard: AdtActionCard,
  offerCard: OfferCard,
  videoSection: VideoSection,
  multiColumnContent: MultiColumnContent,
  twoColumnAccordionMedia: TwoColumnAccordionMedia,
  twoColumnImageBenefits: TwoColumnImageBenefits,
  twoColumnHighestRatedApp: TwoColumnHighestRatedApp,
  flexibleMonitoringAppSection: FlexibleMonitoringAppSection,
  twoColumnPromoSection: TwoColumnPromoSection,
  appInfo: AppInfo,
  appDownloadCallout: AppDownloadCallout,
  articleGroup: ArticleGroup,
  quizSection: QuizSection,
  comparePlanTableSection: ComparePlanTableSection,
  compareTableSection: CompareTableSection,
  compareCompetitorTableSection: CompareCompetitorTableSection,
  appRatingsComparisonTable: AppRatingsComparisonTable,
  optionCardGrid: OptionCardGrid,
  customerReviewsSection: CustomerReviewsSection,
  textBuilder: TextBuilder,
  buttonBuilder: ButtonBuilder,
  spotlightHero: SpotlightHero,
  adtVideoHero: AdtVideoHero,
  adtImageHero: AdtImageHero,
  adtQuizPromo: AdtQuizPromo,
  adtSocialProof: AdtSocialProof,
  adtTwoWays: AdtTwoWays,
  adtMonitoringBanner: AdtMonitoringBanner,
  adtProfessionalMonitoring: AdtProfessionalMonitoring,
  adtAwards: AdtAwards,
  adtFreeQuoteInline: FreeQuoteInline,
  adtLandingFreeQuoteInline: FreeQuoteInline,
  seoParagraph: SeoParagraph,
  contentSection: SeoParagraph,
  adtCustomerReviewsSection: AdtCustomerReviewsSection,
  containerBuilder: ContainerBuilder,
  suggestedForYou: SuggestedForYouBlock,
  buildProtection: BuildProtection,
  carouselSection: CarouselSection,
  testimonials: Testimonials,
  productCardListing: ProductCardListing,
  medicalAlertSystemSection: MedicalAlertSystemSection,
};

type BlocksType = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: React.FC<any>;
};

type BlockType = {
  _type: string;
  _key: string;
};

type BlockProps = {
  index: number;
  block: BlockType;
  pageId: string;
  pageType: string;
  pageName?: string | null;
};
